import {
  Directive,
  HostBinding,
  HostListener,
  Input,
  Optional,
  Self,
  OnChanges,
  OnDestroy,
  DoCheck,
  ElementRef,
  NgZone,
  Inject,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';

import { FormGroupDirective, NgControl } from '@angular/forms';

import { PgFieldControl } from '../pg-field-control';

import { PG_INPUT_VALUE_ACCESSOR } from './input-value-accessor';
import { coerceBooleanProperty } from './coercion';
import { Subscription } from 'rxjs';
import {
  ErrorStateMatcher,
  mixinIsError,
  CanUpdateErrorState,
} from './input-error-options';
import { Platform } from '@angular/cdk/platform';

let nextUniqueId = 0;

export class PgInputBase {
  constructor(
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl
  ) {}
}

export const _PgInputMixinBase = mixinIsError(PgInputBase);

@Directive({
  selector: 'input[pgInput], pgInput, input[gritInput], gritInput',
  exportAs: 'pgInput, gritInput',
  providers: [
    {
      provide: PgFieldControl,
      useExisting: PgInputDirective,
      multi: true,
    },
  ],
})
export class PgInputDirective extends _PgInputMixinBase
  implements
    PgFieldControl<any>,
    OnChanges,
    OnDestroy,
    DoCheck,
    OnInit,
    CanUpdateErrorState {
  protected _uid = `pg-input-${nextUniqueId++}`;
  protected _previousNativeValue: any;
  private _inputValueAccessor: { value: any };

  stateChanges;
  isFocused: boolean;

  get isErrorStateFrozen() {
    return this._isErrorStateFrozen;
  }
  set isErrorStateFrozen(value: boolean) {
    this._isErrorStateFrozen = value;
    this.stateChanges.next();
  }
  protected _isErrorStateFrozen = false;

  controlType = 'pg-input';

  isAutofilled = false;

  get isEmpty(): boolean {
    return (
      !this.elementRef.nativeElement.value &&
      !this._isBadInput() &&
      !this.isAutofilled
    );
  }
  get shouldLabelFloat(): boolean {
    return (
      this.isFocused || !this.isEmpty || this.isError // || this.ngControl.touched
    );
  }

  // #region data-binding

  @HostBinding('class')
  readonly _classList = 'pg-input';

  @HostBinding('attr.id')
  @Input()
  get id() {
    return this._id || this._uid;
  }
  set id(value: string) {
    this._id = value || this._uid;
  }
  protected _id: string;

  // @HostBinding('attr.disabled')
  @Input('disabled')
  get isDisabled() {
    if (this.ngControl && this.ngControl.disabled !== null) {
      return this.ngControl.disabled;
    }
    return this._isDisabled;
  }
  set isDisabled(value: boolean) {
    this._isDisabled = coerceBooleanProperty(value);
    if (this.ngControl) {
      const control = this._parentFormGroup.form.get(this.ngControl
        .name as string);

      if (control && value) {
        control.disable();
      } else {
        control.enable();
      }
    }
    this.stateChanges.next();
  }
  protected _isDisabled = false;

  @HostBinding('required')
  required;

  @HostBinding('attr.aria-describedby')
  _ariaDescribedby: string;

  @HostBinding('attr.aria-invalid')
  isError;

  @HostBinding('attr.aria-labelledby')
  _ariaLabelledby: string;

  @HostBinding('attr.placeholder')
  @Input('placeholder')
  placeholder;

  @HostBinding('class.pg-secure-input')
  @Input()
  secure: boolean = false;

  @HostBinding('attr.type')
  @Input('type')
  get type() {
    return this._type;
  }
  set type(value: string) {
    this._type = value || 'text';
  }
  protected _type = 'text';

  @Input()
  errorStateMatcher: ErrorStateMatcher;

  @Input()
  get value() {
    return this._inputValueAccessor.value;
  }
  set value(value: any) {
    if (value !== this.value) {
      this._inputValueAccessor.value = value;
      this._parentFormGroup.form
        .get(this.ngControl.name as string)
        .setValue(value);
      this.stateChanges.next();
    }
  }

  /** Whether the element is readonly. */
  @HostBinding('readonly')
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: boolean) {
    this._readonly = coerceBooleanProperty(value);
  }
  private _readonly = false;

  // #endregion

  selfCheckSub: Subscription;
  _prevValidity: any;

  constructor(
    public elementRef: ElementRef,
    @Optional()
    @Self()
    public ngControl: NgControl,
    @Optional() _parentFormGroup: FormGroupDirective,
    @Optional()
    @Self()
    @Inject(PG_INPUT_VALUE_ACCESSOR)
    inputValueAccessor: any,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    protected _platform: Platform,
    ngZone: NgZone
  ) {
    super(_defaultErrorStateMatcher, _parentFormGroup, ngControl);

    this._inputValueAccessor = inputValueAccessor || elementRef.nativeElement;

    // On some versions of iOS the caret gets stuck in the wrong place when holding down the delete
    // key. In order to get around this we need to "jiggle" the caret loose. Since this bug only
    // exists on iOS, we only bother to install the listener on iOS.
    if (_platform.IOS) {
      // https://stackoverflow.com/a/25569117/2313300
      // https://html.spec.whatwg.org/multipage/input.html#do-not-apply
      // This is only supported on specific input types
      const supportedInputTypes = ['text', 'tel', 'search', 'password', 'url'];
      ngZone.runOutsideAngular(() => {
        elementRef.nativeElement.addEventListener('keyup', (event: Event) => {
          const el = event.target as HTMLInputElement;
          const canSelect = supportedInputTypes.indexOf(el.type) > -1;
          if (
            !el.value &&
            !el.selectionStart &&
            !el.selectionEnd &&
            canSelect
          ) {
            // Note: Just setting `0, 0` doesn't fix the issue. Setting
            // `1, 1` fixes it for the first time that you type text and
            // then hold delete. Toggling to `1, 1` and then back to
            // `0, 0` seems to completely fix it.
            el.setSelectionRange(1, 1);
            el.setSelectionRange(0, 0);
          }
        });
      });
    }

    // Autofill monitor
    const cssClass = 'autofilled';
    ngZone.runOutsideAngular(() => {
      elementRef.nativeElement.addEventListener(
        'animationstart',
        (evt: AnimationEvent) => {
          if (
            evt.animationName === 'onAutoFillStart' &&
            !elementRef.nativeElement.classList.contains(cssClass)
          ) {
            elementRef.nativeElement.classList.add(cssClass);
            ngZone.run(() => {
              this.isAutofilled = true;
              this.stateChanges.next();
            });
          }
        }
      );
      elementRef.nativeElement.classList.add('autofillMonitored');
    });
    // --
  }

  @HostListener('focus')
  onFocus() {
    this._focusChanged(true);
  }

  @HostListener('blur', [])
  onBlur() {
    this._focusChanged(false);
  }

  @HostListener('input')
  onInput() {
    // This is a noop function and is used to let Angular know whenever the value changes.
    // Angular will run a new change detection each time the `input` event has been dispatched.
    // It's necessary that Angular recognizes the value change, because when floatingLabel
    // is set to false and Angular forms aren't used, the placeholder won't recognize the
    // value changes and will not disappear.
    // Listening to the input event wouldn't be necessary when the input is using the
    // FormsModule or ReactiveFormsModule, because Angular forms also listens to input events.
  }

  @HostListener('change')
  onChange() {}

  ngOnInit() {
    if (this.ngControl) {
      this.selfCheckSub = this.ngControl.statusChanges.subscribe(status => {
        if (this._prevValidity !== status) {
          this._prevValidity = status;
          this.stateChanges.next();
        }
      });
    }
  }

  ngOnChanges() {
    this.stateChanges.next();
  }

  ngOnDestroy() {
    if (this.selfCheckSub) {
      this.selfCheckSub.unsubscribe();
    }
    this.stateChanges.complete();
  }

  ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this.updateIsError();
    }
    // We need to dirty-check the native element's value, because there are some cases where
    // we won't be notified when it changes
    this._dirtyCheckNativeValue();
  }

  focus(selectionStart?, selectionEnd?) {
    this.elementRef.nativeElement.focus();
    if (
      (selectionStart || selectionStart === 0) &&
      (selectionEnd || selectionEnd === 0)
    ) {
      this.elementRef.nativeElement.setSelectionRange(
        selectionStart,
        selectionEnd
      );
    }
  }

  blur() {
    this.elementRef.nativeElement.blur();
  }

  private _focusChanged(isFocused: boolean) {
    if (isFocused !== this.isFocused) {
      this.isFocused = isFocused;
      this.stateChanges.next();
    }
  }

  protected _dirtyCheckNativeValue() {
    const newValue = this.value;

    if (this._previousNativeValue !== newValue) {
      this._previousNativeValue = newValue;
      this.stateChanges.next();
    }
  }
  /** Checks whether the input is invalid based on the native validation. */
  protected _isBadInput() {
    const validity = (this.elementRef.nativeElement as HTMLInputElement)
      .validity;
    return validity && validity.badInput;
  }

  /** Determines if the component host is a textarea. */
  protected _isTextarea() {
    return this.elementRef.nativeElement.nodeName.toLowerCase() === 'textarea';
  }

  setDescribedByIds(ids: string[]) {
    this._ariaDescribedby = ids.join(' ');
  }

  onContainerClick() {
    this.focus();
  }
}
